import React, { useCallback, useMemo } from "react";
import { Button, FolderWrapper } from "components";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import { UncontrolledTooltip } from "reactstrap";
import { ProductConfiguratorModal } from "objs/_ConfiguratorObjs/Wizard/ProductConfiguratorModal/ProductConfiguratorModal";
import { ProductListModal } from "components/_Modals/ProductListModal";
import { Icon } from "components/Icon/Icon";
import {
  useSelectedTopologyItemId,
  useSubType,
  useTopology,
  useWizardActions,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import s from "./WizardLeftSidebarExtension.module.scss";

export const WizardTopologyMenuFolder = React.memo(
  ({
    hasChildren,
    id,
    title,
    translations,
    orderCode = {},
    presentation,
    dummy,
    type,
    handleToggle,
    isOpen,
    level,
    menu,
    selectedAccessories,
    groupPosition,
  }) => {
    const { setCenterTopologyItemId, setSelectedTopologyItemId } =
      useWizardActions();
    const subType = useSubType();
    const topology = useTopology();
    const selectedTopologyItemId = useSelectedTopologyItemId();
    const { seriesId } = orderCode;

    const modalProps = useMemo(
      () => ({
        id,
        orderCode,
        title,
        dummy,
        type,
        wizardType: subType,
        seriesId,
        topology,
        selectedAccessories,
        groupPosition,
      }),
      [
        id,
        orderCode,
        title,
        dummy,
        type,
        subType,
        seriesId,
        topology,
        selectedAccessories,
        groupPosition,
      ],
    );

    const handleBackAction = useCallback(() => {
      // eslint-disable-next-line react-hooks/immutability
      NiceModal.show(ProductListModal, { ...modalProps, handleBackAction });
    }, [modalProps]);

    const openConfigurationModal = useCallback(() => {
      NiceModal.show(seriesId ? ProductConfiguratorModal : ProductListModal, {
        ...modalProps,
        handleBackAction,
      });
    }, [modalProps, seriesId, handleBackAction]);

    return (
      <FolderWrapper
        hasChildren={hasChildren}
        translations={translations}
        className={cx(
          s.wizardLeftSidebarExtFolder,

          id === selectedTopologyItemId
            ? s.wizardLeftSidebarExtFolderActive
            : "border",
        )}
        id={`ls-topology-item-${id}`} // ls means "Left Sidebar"
        onClick={() => {
          setCenterTopologyItemId(id);
          setSelectedTopologyItemId(id);
        }}
      >
        {hasChildren && level !== 0 ? (
          <Icon
            onClick={handleToggle}
            iconName={isOpen ? "fa-chevron-down" : "fa-chevron-right"}
            className="icon-16 mx-2 cursor-pointer position-relative"
          />
        ) : (
          <div className="ms-1" />
        )}
        <div className={cx(s.wizardLeftSidebarExtFolderData, "d-flex")}>
          <h6
            className={cx(
              "mb-0 fs-md text-white overflow-x-hidden text-nowrap text-truncate",
              { "ms-2": !hasChildren },
            )}
            id={`title${id}`}
          >
            {title}
          </h6>
          <UncontrolledTooltip target={`title${id}`} placement="top">
            {title}
          </UncontrolledTooltip>
          {type !== "Hardware" && (
            <>
              <span
                className={cx(
                  s.wizardLeftSidebarExtFolderPresentation,
                  "ms-2 fs-md overflow-hidden text-nowrap text-truncate",
                  {
                    "text-warning": presentation === "Dummy",
                    "text-gray-500": presentation !== "Dummy",
                  },
                )}
                id={`presentation${id}`}
              >
                {presentation}
              </span>
              <UncontrolledTooltip target={`presentation${id}`} placement="top">
                {presentation}
                {selectedAccessories?.map((item, key) => (
                  <div key={key}>{item.currentPresentation}</div>
                ))}
              </UncontrolledTooltip>
            </>
          )}
        </div>
        {/* TODO: workaround for hiding Configure button for X-CUBE, BMS, GLT, DIO;
        from BE we would need info which nodes are configurable and which not */}
        <div
          className="position-absolute h-100 d-flex align-items-center"
          style={{ right: 0, top: 0, zIndex: 4 }}
        >
          {menu?.some((item) => item.id === 1 && item.enabled) && (
            <Button
              color="link"
              classNames={cx(
                "px-1",
                s.wizardLeftSidebarExtFolderConfigureButton,
              )}
              iconPrefix="trox"
              icon="configure-regular"
              onClick={openConfigurationModal}
              data-testid="wizard-topology-menu-folder-configure-icon"
            />
          )}
        </div>
      </FolderWrapper>
    );
  },
);
